import {
  ChangePasswordFormValues,
  CreateUserFormValues,
  UpdateUserFormValues,
  User,
} from "@/types/user";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import {
  changeUserPassword,
  createUser,
  deleteUser,
  getRoles,
  getUsers,
  updateUser,
} from "../api/user";
import Role from "@/types/role";

interface UseUserMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetUsers = (config?: AxiosRequestConfig) => {
  return useQuery<
    PaginatedApiResponse<User[]>,
    Error,
    { data: User[]; pagination: PaginationInfo }
  >({
    queryKey: ["users", config?.params],
    queryFn: () => getUsers(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
  });
};

export const useGetUsersOptions = (config?: AxiosRequestConfig) => {
  return useQuery<ApiResponse<User[]>, Error, User[]>({
    queryKey: ["users", config?.params],
    queryFn: () => getUsers(config),
    select: (response) => response.data,
  });
};

export const useCreateUser = (options?: UseUserMutationOptions<User>) => {
  return useMutation<User, Error, CreateUserFormValues>({
    mutationFn: (user) => createUser(user),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useUpdateUser = (options?: UseUserMutationOptions<User>) => {
  return useMutation<User, Error, UpdateUserFormValues>({
    mutationFn: (user) => updateUser(user.id, user),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useDeleteUser = (options?: UseUserMutationOptions<User>) => {
  return useMutation<User, Error, string>({
    mutationFn: (id) => deleteUser(id),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useGetRoles = (config?: AxiosRequestConfig) => {
  return useQuery<ApiResponse<Role[]>, Error, Role[]>({
    queryKey: ["roles", config?.params],
    queryFn: () => getRoles(config),
    select: (response) => response.data,
  });
};

export const useChangeUserPassword = (
  options?: UseUserMutationOptions<User>
) => {
  return useMutation<User, Error, ChangePasswordFormValues>({
    mutationFn: (user) => changeUserPassword(user.id, user),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};
